/**
 * Marketing attribution URL parameters that must survive page navigation until
 * a successful booking POST. Only these keys are ever read, written, or
 * persisted by this module. Any other params already present on a URL are left
 * untouched.
 */

export const MARKETING_PARAM_KEYS = [
  // Google click-ids
  'gclid',
  'gbraid',
  'wbraid',
  // Meta / Microsoft / TikTok
  'fbclid',
  'msclkid',
  'ttclid',
  // OpenAI / ChatGPT Ads click-id
  'oiaclid',
  // UTM standard
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  // Google Ads value-track
  'source',
  'medium',
  'campaignid',
  'adgroupid',
  'creative',
  'matchtype',
  'network',
  'device',
  'keyword',
  'gad_source',
  'gad_campaignid',
  // Commission Junction Affiliate
  'cjevent',
  'cjdata',
  'AID',
  'PID',
  // Passthrough: Salesforce context and B2B partnership
  'sfId',
  'sfObjectType',
  'partnershipCode',
] as const

export type MarketingParamKey = typeof MARKETING_PARAM_KEYS[number]

/**
 * Internal key that records the original ad landing URL (origin + pathname).
 * Namespaced to avoid colliding with native/analytics `referrer` semantics. It
 * is sent to the backend on booking confirmation as the `referrer` property.
 */
export const TW_REFERRER_KEY = 'tw_referrer'

/**
 * All keys this module owns in the URL. These are the only keys it will append
 * or strip.
 */
export const PERSISTED_PARAM_KEYS: string[] = [
  ...MARKETING_PARAM_KEYS,
  TW_REFERRER_KEY,
]

/**
 * Click-id keys grouped by ad platform. Used by the override logic: when a user
 * arrives from a different platform, the previous platform's click-ids are
 * cleared so attribution reflects the latest ad entry.
 */
export const PLATFORM_CLICK_ID_KEYS: Record<string, MarketingParamKey[]> = {
  google: ['gclid', 'gbraid', 'wbraid'],
  meta: ['fbclid'],
  microsoft: ['msclkid'],
  tiktok: ['ttclid'],
  openai: ['oiaclid'],
  cj: ['cjevent', 'cjdata', 'AID', 'PID'],
}

/**
 * Keys that are persisted and re-appended to URLs like click-ids and UTM, but
 * are NOT ad attribution signals. They are copied as-is on merge and do not
 * trigger a new-ad-arrival or tw_referrer refresh.
 */
export const PASSTHROUGH_KEYS: MarketingParamKey[] = [
  'sfId',
  'sfObjectType',
  'partnershipCode',
]

export const ALL_CLICK_ID_KEYS: MarketingParamKey[] = Object.values(
  PLATFORM_CLICK_ID_KEYS
).flat()

export const GOOGLE_ADS_KEYS: MarketingParamKey[] = [
  'source',
  'medium',
  'campaignid',
  'adgroupid',
  'creative',
  'matchtype',
  'network',
  'device',
  'keyword',
  'gad_source',
  'gad_campaignid',
]

export const UTM_KEYS: MarketingParamKey[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]

export type MarketingParams = Partial<Record<MarketingParamKey, string>>

/**
 * Global set by the early `beforeInteractive` snapshot script, capturing
 * `location.search` before React hydration can strip it.
 */
export const TW_MARKETING_PARAMS_GLOBAL = '__twMarketingParams'
